import type { ID } from "../../../lib/id";
/**
 * Moving a row one place up or down.
 *
 * The API stores an explicit `sort_order` per row and the PATCH
 * endpoints replace a row wholesale, so a move is expressed as the set
 * of rows whose `sort_order` has to change. Renumbering the entire list
 * from its new order (rather than swapping two values) also repairs rows
 * that share a `sort_order` — which every row does until someone first
 * reorders them.
 */
export function reorderRows<T extends { id: ID; sort_order: number }>(
  rows: T[],
  index: number,
  direction: -1 | 1,
): { row: T; sort_order: number }[] {
  const target = index + direction;
  if (index < 0 || target < 0 || target >= rows.length) return [];
  const next = [...rows];
  [next[index], next[target]] = [next[target], next[index]];
  return next
    .map((row, position) => ({ row, sort_order: position }))
    .filter(({ row, sort_order }) => row.sort_order !== sort_order);
}
